import { RefObject, useImperativeHandle, useMemo } from 'react';
import { useIsomorphicLayoutEffect } from '@direct-frontend/stdlib';

import type { GridHandle, GridRef } from '../../types';

interface WindowKeepMountedGridEffectsProps {
    gridRef: RefObject<HTMLDivElement>;
    innerRef: RefObject<GridHandle>;
    forwardedRef: GridRef;
    updateOffset: (offset: number) => void;
    onScroll: VoidFunction;
}

// Монтируется после тела грида: callback-ref может сразу вызвать методы внутреннего виртуализатора.
export function WindowKeepMountedGridEffects({
    gridRef,
    innerRef,
    forwardedRef,
    updateOffset,
    onScroll,
}: WindowKeepMountedGridEffectsProps) {
    // Стабильный публичный объект читает актуальный диапазон, не перезапуская потребителей callback-ref.
    const publicHandle = useMemo<GridHandle>(
        () => ({
            scrollCellToIndex: (index, options) => innerRef.current?.scrollCellToIndex(index, options),
            scrollRowToIndex: (index, options) => innerRef.current?.scrollRowToIndex(index, options),
            scrollToOffset: (offset, options) => innerRef.current?.scrollToOffset(offset, options),
            getFirstVisibleRowIndex: () => innerRef.current?.getFirstVisibleRowIndex() ?? 0,
            horizontalScrollToOffset: offset => innerRef.current?.horizontalScrollToOffset(offset),
            isScrollableByY: () => innerRef.current?.isScrollableByY() ?? false,
            isScrolledToEndByY: () => innerRef.current?.isScrolledToEndByY() ?? false,
            getGridDimensions: () => innerRef.current?.getGridDimensions() ?? { height: 0, width: 0 },
        }),
        [innerRef]
    );
    useImperativeHandle(forwardedRef, () => publicHandle, [publicHandle]);

    useIsomorphicLayoutEffect(() => {
        const ownerWindow = gridRef.current?.ownerDocument.defaultView;
        if (!ownerWindow) {
            return;
        }

        const onWindowScroll = () => {
            updateOffset(ownerWindow.scrollY);
            onScroll();
        };

        updateOffset(ownerWindow.scrollY);
        ownerWindow.addEventListener('scroll', onWindowScroll, { passive: true });
        return () => ownerWindow.removeEventListener('scroll', onWindowScroll);
    }, [gridRef, onScroll, updateOffset]);

    return null;
}
